/**
 * Sheet „Zugriff" am Objekt (`access-sheet`, Spec 05a, „Sheet Zugriff am
 * Objekt"). Geöffnet über die Kopfaktion `object-access` in
 * `routes/ObjectDetail.tsx` — kein sechster Reiter (ADR 0011).
 *
 * Besitzt drei Sheets zugleich, wie `routes/ObjectDetail.tsx` es für
 * Vertrag/Mieter tut: dieses (`access-sheet`), „Zugriff geben"
 * (`AccessAddGrantSheet`) und, darüber erreichbar, „Person einladen"
 * (`AccessInviteSheet`, `presetEntity` gesetzt) — `access-add-invite` wandert
 * von einem ins andere, weil beide hier zusammengesetzt werden
 * (`features/access` kennt sich selbst, aber zwei Sheets, die sich
 * gegenseitig öffnen, gehören an den gemeinsamen Aufrufer).
 */

import { Link } from '@tanstack/react-router'
import { useState } from 'react'

import { ConfirmationStrip } from '@/components/ConfirmationStrip'
import { EmptyState } from '@/components/states/EmptyState'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { ApiError } from '@/lib/api'
import { formatInstantInBerlin } from '@/lib/date'
import { useAccessGrants, useDeleteGrant, useRoles, useUpdateGrant } from '@/features/access/api'
import type { GrantItem } from '@/features/access/api'
import { AccessAddGrantSheet } from '@/features/access/AccessAddGrantSheet'
import { AccessInviteSheet } from '@/features/access/AccessInviteSheet'
import { FIELD_CLASS } from '@/components/FormField'

interface AccessSheetProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly entityId: string
  readonly entityLabel: string
}

interface PendingRevoke {
  readonly userId: string
  readonly displayName: string
}

export function AccessSheet({ open, onOpenChange, entityId, entityLabel }: AccessSheetProps) {
  const grantsQuery = useAccessGrants(entityId, { enabled: open })
  const deleteMutation = useDeleteGrant()

  const [isAddOpen, setIsAddOpen] = useState(false)
  const [isInviteOpen, setIsInviteOpen] = useState(false)
  const [pendingRevoke, setPendingRevoke] = useState<PendingRevoke | null>(null)
  const [rowError, setRowError] = useState<string | undefined>(undefined)

  async function handleRevokeConfirm() {
    if (pendingRevoke === null) return
    try {
      await deleteMutation.mutateAsync({ entityId, userId: pendingRevoke.userId })
      setPendingRevoke(null)
    } catch (error) {
      setPendingRevoke(null)
      setRowError(error instanceof ApiError ? error.message : 'Die Verbindung zum Server ist unterbrochen.')
    }
  }

  const directUserIds = (grantsQuery.data?.items ?? [])
    .filter((item) => item.source.value === 'direct')
    .map((item) => item.user_id)

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          showCloseButton={false}
          className="data-[side=right]:!w-full data-[side=right]:sm:!max-w-none md:data-[side=right]:!w-[480px] md:data-[side=right]:!max-w-[480px]"
        >
          <div data-testid="access-sheet" className="flex h-full flex-col">
            <SheetHeader>
              <SheetTitle className="text-section font-(--font-weight-section) text-text">
                Zugriff auf „{entityLabel}"
              </SheetTitle>
            </SheetHeader>

            <div className="flex flex-1 flex-col overflow-y-auto">
              {rowError !== undefined && (
                <p role="alert" className="text-label text-status-overdue px-4 pt-2">
                  {rowError}
                </p>
              )}

              {grantsQuery.isPending && <SkeletonRows testId="access-list-loading" rows={4} />}

              {grantsQuery.isError && (
                <ErrorState
                  testId="access-list-error"
                  retryTestId="access-list-retry"
                  error={grantsQuery.error}
                  onRetry={() => {
                    void grantsQuery.refetch()
                  }}
                />
              )}

              {grantsQuery.isSuccess && grantsQuery.data.items.length === 0 && (
                <EmptyState
                  testId="access-list-empty"
                  title="Nur du hast Zugriff."
                  action={
                    grantsQuery.data.can_manage && (
                      <button
                        type="button"
                        data-testid="access-add"
                        onClick={() => {
                          setIsAddOpen(true)
                        }}
                        className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out)"
                      >
                        Zugriff geben
                      </button>
                    )
                  }
                />
              )}

              {grantsQuery.isSuccess && grantsQuery.data.items.length > 0 && (
                <ul>
                  {grantsQuery.data.items.map((grant) => (
                    <GrantRow
                      // Direkter **und** geerbter Zugriff derselben Person sind
                      // laut `_LIST_GRANTS_SQL` (`access/grants.py`) zwei
                      // Zeilen — `grant.user_id` allein ist dann kein
                      // eindeutiger React-Key mehr (W11, `findings.md`).
                      // `source_entity_id` unterscheidet die beiden immer:
                      // die direkte Zeile trägt `entityId`, die geerbte einen
                      // Vorfahren.
                      key={`${grant.user_id}-${grant.source_entity_id}`}
                      grant={grant}
                      entityId={entityId}
                      isAmbiguousUser={
                        grantsQuery.data.items.filter((item) => item.user_id === grant.user_id).length > 1
                      }
                      onRequestRevoke={(userId, displayName) => {
                        setPendingRevoke({ userId, displayName })
                      }}
                      onRoleError={setRowError}
                      onCloseSheet={() => {
                        onOpenChange(false)
                      }}
                    />
                  ))}
                </ul>
              )}

              {grantsQuery.isSuccess && grantsQuery.data.items.length > 0 && grantsQuery.data.can_manage && (
                <div className="p-(--page-x)">
                  <button
                    type="button"
                    data-testid="access-add"
                    onClick={() => {
                      setIsAddOpen(true)
                    }}
                    className="border-border text-text hover:bg-bg rounded-card text-body w-full border px-4 transition-colors duration-(--duration-fast) ease-(--ease-out)"
                  >
                    Zugriff geben
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Innerhalb von `SheetContent`, nicht als Geschwister der Route:
           * Radix' `Dialog` markiert bei offenem Modal alles außerhalb seines
           * eigenen Portals als `aria-hidden`/`pointer-events: none` (geprüft
           * beim Aufnehmen der Bildschirmfotos für diesen Lauf — „Entziehen"
           * fragte zurück, aber kein Klick auf `access-revoke-cancel` oder
           * `-confirm` kam durch). `position: fixed` platziert den Streifen
           * trotz der Verschachtelung weiterhin am unteren Bildschirmrand. */}
          <ConfirmationStrip
            testId="access-revoke-confirmation"
            message={
              pendingRevoke !== null
                ? `Zugriff von ${pendingRevoke.displayName} auf ${entityLabel} entziehen?`
                : null
            }
            onDismiss={() => {
              setPendingRevoke(null)
            }}
            confirm={{
              confirmLabel: 'Entziehen',
              confirmTestId: 'access-revoke-confirm',
              cancelLabel: 'Abbrechen',
              cancelTestId: 'access-revoke-cancel',
              onConfirm: () => void handleRevokeConfirm(),
            }}
          />
        </SheetContent>
      </Sheet>

      <AccessAddGrantSheet
        open={isAddOpen}
        onOpenChange={setIsAddOpen}
        entityId={entityId}
        entityLabel={entityLabel}
        excludeIds={directUserIds}
        onRequestInvite={() => {
          setIsAddOpen(false)
          setIsInviteOpen(true)
        }}
      />

      <AccessInviteSheet
        open={isInviteOpen}
        onOpenChange={setIsInviteOpen}
        presetEntity={{ id: entityId, label: entityLabel }}
      />
    </>
  )
}

interface GrantRowProps {
  readonly grant: GrantItem
  readonly entityId: string
  /** Trägt dieselbe Person noch eine zweite Zeile (direkt **und** geerbt),
   * reicht `access-row-<user_id>` allein nicht mehr zur eindeutigen
   * Ansprache (W11, `findings.md`). Der Regelfall — eine Zeile je Person —
   * behält den bisherigen Bezeichner unverändert. */
  readonly isAmbiguousUser: boolean
  readonly onRequestRevoke: (userId: string, displayName: string) => void
  readonly onRoleError: (message: string | undefined) => void
  readonly onCloseSheet: () => void
}

function GrantRow({ grant, entityId, isAmbiguousUser, onRequestRevoke, onRoleError, onCloseSheet }: GrantRowProps) {
  const rolesQuery = useRoles()
  const updateMutation = useUpdateGrant()
  const isExpired = grant.state.value !== 'active'
  const displayName = grant.display_name ?? grant.email

  async function handleRoleChange(role: string) {
    onRoleError(undefined)
    try {
      await updateMutation.mutateAsync({
        entityId,
        userId: grant.user_id,
        body: { role },
        ifMatch: String(grant.version),
      })
    } catch (error) {
      onRoleError(error instanceof ApiError ? error.message : 'Die Verbindung zum Server ist unterbrochen.')
    }
  }

  return (
    <li
      data-testid={isAmbiguousUser ? `access-row-${grant.user_id}-${grant.source.value}` : `access-row-${grant.user_id}`}
      className={`border-border flex flex-col gap-1 border-b px-(--page-x) py-2 md:flex-row md:items-center md:gap-3 ${isExpired ? 'opacity-60' : ''}`}
    >
      <span className="flex min-w-0 flex-col md:flex-1">
        <span className="text-body text-text truncate">{displayName}</span>
        <span className="text-label text-muted truncate">{grant.email}</span>
        {/* K9 (`design-reviewer`): `expires_at` kam vom Server, aber keine
         * Zeile zeigte es — wer ein Ablaufdatum setzte, sah es nie wieder.
         * `formatInstantInBerlin` liest den Zeitpunkt als Berliner
         * Kalendertag zurück, denselben, den `berlinEndOfDayIso` beim
         * Speichern gemeint hat (Regel 9). */}
        {grant.expires_at !== null && (
          <span data-testid={`access-expires-${grant.user_id}`} className="text-label text-muted truncate">
            Letzter Tag mit Zugriff: {formatInstantInBerlin(grant.expires_at)}
          </span>
        )}
      </span>

      {isExpired && <span className="text-label text-muted shrink-0">{grant.state.label}</span>}

      {grant.can_edit ? (
        <select
          data-testid={`access-role-${grant.user_id}`}
          value={grant.role.value}
          disabled={updateMutation.isPending || rolesQuery.isPending}
          onChange={(event) => {
            void handleRoleChange(event.target.value)
          }}
          className={`text-text shrink-0 ${FIELD_CLASS}`}
        >
          {(rolesQuery.data ?? []).map((role) => (
            <option key={role.value} value={role.value}>
              {role.label}
            </option>
          ))}
        </select>
      ) : (
        <span className="text-label text-muted flex shrink-0 flex-wrap items-center gap-1">
          {grant.role.label}
          <span>· geerbt von</span>
          <Link
            to="/objekt/$entityId"
            params={{ entityId: grant.source_entity_id }}
            search={{ tab: undefined }}
            data-testid={`access-source-${grant.user_id}`}
            onClick={onCloseSheet}
            className="text-accent hover:underline"
          >
            {grant.source_entity_label}
          </Link>
        </span>
      )}

      {grant.can_edit && (
        <button
          type="button"
          data-testid={`access-revoke-${grant.user_id}`}
          onClick={() => {
            onRequestRevoke(grant.user_id, displayName)
          }}
          className="text-status-overdue text-label shrink-0 hover:underline"
        >
          Entziehen
        </button>
      )}
    </li>
  )
}
